import React, { useEffect, useRef } from 'react';
import { ButtonIcon, Icon, InputField } from '@saviynt/design-system';
import cx from 'classnames';

import { useIspmContext } from '../../context/ispmContext';
import { Logger } from '../../services';
import {
  DASHBOARD_HEADER_SAVI_INPUTBOX,
  DASHBOARD_HEADER_SAVI_RESUMEICON,
} from '../../tests/testids';
import { useInvertedLocalize } from '../../utils/localization';
import { invertedMessages } from '../../utils/messages';

import './SaviInputField.css';

interface SaviInputFieldProps {
  isMenuPinned: boolean;
}

SaviInputField.default = {
  isMenuPinned: false,
};

function SaviInputField({ isMenuPinned }: SaviInputFieldProps): JSX.Element {
  // ISPM related context values
  const { ispmState, updateIspmState } = useIspmContext();

  const l = useInvertedLocalize(invertedMessages);

  const inputRef = useRef<HTMLInputElement>(null);

  const setIspmInputValue = (inputText: string) => {
    updateIspmState('ispmInputValue', inputText);
  };

  function handleUserQuerySearch() {
    Logger.info('Handling header savi icon or "input enter" click', {
      text: ispmState.ispmInputValue,
    });
    updateIspmState('isChatOverlayVisible', true);
    updateIspmState('isSuggestionVisible', false);
    updateIspmState('userQuery', ispmState.ispmInputValue);
  }

  useEffect(() => {
    const inputElement = inputRef.current;

    // Function to handle focus event
    const handleFocus = () => {
      updateIspmState('isSuggestionVisible', true);
    };

    if (inputElement) {
      // Add event listeners for focus and blur
      inputElement.addEventListener('focus', handleFocus);
    }

    return () => {
      if (inputElement) {
        inputElement.removeEventListener('focus', handleFocus);
      }
    };
  }, [updateIspmState]);

  const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Enter') {
      handleUserQuerySearch();
    }
  };

  return (
    <div
      className={cx(
        'Ispm-SaviInputfieldContainer',
        isMenuPinned && 'Ispm-SaviInputfieldContainer--isSideMenuPinned'
      )}>
      <div className='Ispm-SaviInputfieldWrapper'>
        <InputField
          ref={inputRef}
          name='Ispm-SaviInputfield'
          size='small'
          placeholder={l('What can I help you with?')}
          prefixIcon={
            <Icon kind='Comment' color='purple-700' size='smallMedium' />
          }
          className='Ispm-SaviInputfield'
          value={ispmState.ispmInputValue}
          setValue={setIspmInputValue}
          onKeyDown={handleKeyDown}
          dataTestId={DASHBOARD_HEADER_SAVI_INPUTBOX}
        />
      </div>
      <div className='Ispm-SaviInputfield-icon'>
        <ButtonIcon
          size='medium'
          kind='saviGhost'
          icon={<Icon kind='SaviCompanion' size='medium' color='purple-700' />}
          dataTestId={DASHBOARD_HEADER_SAVI_RESUMEICON}
          onClick={() => {
            handleUserQuerySearch();
          }}
        />
      </div>
    </div>
  );
}

export default SaviInputField;
